/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import MsDyn365, { IDictionary, IImageData, IImageSettings, Image } from '@msdyn365-commerce/core';
import { IMenuItemData, INavigationMenuViewProps } from '@msdyn365-commerce-modules/navigation-menu';
import { ArrayExtensions, generateImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import {
    Drawer,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    ICollapseProps,
    IDrawerState,
    IPayLoad,
    ITelemetryContent,
    Module,
    Node,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { isViewportBelow } from '../../../modules/components';
type INavigationMenuExtViewProps = INavigationMenuViewProps & {
    config: INavigationMenuViewProps['config'] & { menuStyle?: 'responsive' | 'drawer' };
};

interface INavigationState {
    parentMenu?: number;
    activeMenu?: number;
    isClosing?: boolean;
    isMobileOverride?: boolean;
    categoryImage?: IImageData[] | null;
    categoryImageAltText: string;
    drawerKeyValue: IDictionary<boolean>;
    drawerPath: number[];
}

// Keep nav mobile/desktop switch aligned with the theme's `m` breakpoint
// so we never end up with a hamburger panel containing the desktop nav
// (or vice versa). SDK gridSettings can sit at a different boundary.

/**
 *
 * NavigationMenuView component.
 * @extends {React.PureComponent<INavigationMenuViewProps>}
 */
export class NavigationMenuView extends React.PureComponent<INavigationMenuExtViewProps, INavigationState> {
    private currentLevel: number = 0;

    private readonly drawerPanels: React.RefObject<HTMLDivElement> = React.createRef();

    private readonly drawerButtons: Map<string, HTMLButtonElement> = new Map();

    private closeTimeout: ReturnType<typeof setTimeout> | null = null;

    private readonly _positionInSetOffset: number = 1;

    private readonly escapeKey: number = 27;

    private readonly menuNode: React.RefObject<HTMLUListElement>;

    private readonly telemetryContent: ITelemetryContent;

    private readonly payLoad: IPayLoad;

    constructor(props: INavigationMenuExtViewProps) {
        super(props);
        this.menuNode = React.createRef();
        this.state = {
            activeMenu: undefined,
            parentMenu: undefined,
            categoryImageAltText: '',
            drawerKeyValue: {},
            drawerPath: [],
            isMobileOverride: undefined
        };
        this._closeSubmenu = this._closeSubmenu.bind(this);
        this._escFunction = this._escFunction.bind(this);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.payLoad = getPayloadObject('click', this.telemetryContent, '', '');
    }

    public componentDidMount(): void {
        if (MsDyn365.isBrowser) {
            document.body.addEventListener('keydown', (this._escFunction as unknown) as EventListener, false);
            document.body.addEventListener('mousedown', this._handleClickOutside);
            document.body.addEventListener('focusout', this._handleFocusOutside);
            window.addEventListener('resize', this._handleResize);
            this._handleResize();
        }
    }

    public componentWillUnmount(): void {
        if (this.closeTimeout) {
            clearTimeout(this.closeTimeout);
        }
        if (MsDyn365.isBrowser) {
            document.body.removeEventListener('keydown', (this._escFunction as unknown) as EventListener, false);
            document.body.removeEventListener('mousedown', this._handleClickOutside, false);
            document.body.removeEventListener('focusout', this._handleFocusOutside, false);
            window.removeEventListener('resize', this._handleResize);
        }
    }

    private get _isMobile(): boolean {
        return this.state.isMobileOverride !== undefined ? this.state.isMobileOverride : this.props.isMobileView;
    }

    private readonly _handleResize = (): void => {
        const isMobile = isViewportBelow('m');
        if (this.state.isMobileOverride !== isMobile) {
            this.setState({ isMobileOverride: isMobile });
        }
    };

    private get _isDrawerStyle(): boolean {
        return this.props.config.menuStyle === 'drawer';
    }

    private get _drawerMaxDepth(): number {
        const { enableMultilevelMenu, menuLevelSupport } = this.props.config;
        const defaultDepth = 3;
        return enableMultilevelMenu ? Math.round(menuLevelSupport || defaultDepth) : 2;
    }

    public render(): JSX.Element | null {
        if (this._isDrawerStyle) {
            return this._renderDrillDown();
        }
        this.currentLevel = 1;
        return <>{this._isMobile ? this._renderMobileMenu() : this._renderDesktopMenu()}</>;
    }

    private _renderDrillDown(): JSX.Element {
        const { menuItemData, Navigation, config, resources } = this.props;
        const { drawerPath } = this.state;
        const panels: JSX.Element[] = [
            <ul key='root' className='ms-nav__panel ms-nav__panel--root' aria-hidden={drawerPath.length > 0}>
                {this._renderDrawerItems(menuItemData, [])}
            </ul>
        ];

        let items: IMenuItemData[] = menuItemData;
        for (let depth = 1; depth <= drawerPath.length; depth++) {
            const parent = items[drawerPath[depth - 1]];
            if (!parent) {
                break;
            }
            const panelPath = drawerPath.slice(0, depth);
            const isActive = depth === drawerPath.length;
            panels.push(
                <ul
                    key={panelPath.join('-')}
                    className={classnames('ms-nav__panel ms-nav__panel--sub', isActive && 'is-active')}
                    aria-hidden={!isActive}
                >
                    <li className='ms-nav__back'>
                        <button type='button' className='ms-nav__back-button' onClick={this._drawerPop}>
                            <span>{resources.backButtonAriaLabel}</span>
                        </button>
                    </li>
                    {parent.linkURL && (
                        <li className='ms-nav__all'>
                            <a className='ms-nav__all-link' href={parent.linkURL} {...this._drawerLinkAttributes(parent)}>
                                <small>{resources.allCategoryMenuText}</small>
                                <span>{parent.linkText}</span>
                            </a>
                        </li>
                    )}
                    {this._renderDrawerItems(this._drawerChildren(parent), panelPath)}
                </ul>
            );
            items = this._drawerChildren(parent);
        }

        return (
            <Module
                {...Navigation}
                tag='nav'
                className={classnames('ms-nav', 'ms-nav--drawer', config.className)}
                data-level={drawerPath.length}
                aria-label={resources.menuAriaLabel}
            >
                <div className='ms-nav__panels' ref={this.drawerPanels}>
                    {panels}
                </div>
            </Module>
        );
    }

    // Module injects its own "all" child; the sub-panel header already covers it
    private _drawerChildren(parent: IMenuItemData): IMenuItemData[] {
        const { allCategoryMenuText } = this.props.resources;
        return (parent.subMenu || []).filter(
            (item: IMenuItemData) => item.linkText && !(item.linkText === allCategoryMenuText && item.linkURL === parent.linkURL)
        );
    }

    private _renderDrawerItems(items: IMenuItemData[], panelPath: number[]): JSX.Element[] {
        const { drawerPath } = this.state;
        const canDrill = panelPath.length + 1 < this._drawerMaxDepth;
        return items.map((item: IMenuItemData, index: number) => {
            const key = [...panelPath, index].join('-');
            let content: JSX.Element;
            if (canDrill && ArrayExtensions.hasElements(item.subMenu)) {
                content = (
                    <button
                        type='button'
                        className='ms-nav__item-button'
                        aria-expanded={drawerPath[panelPath.length] === index}
                        ref={this._setDrawerButtonRef(key)}
                        onClick={this._drawerPush(index)}
                    >
                        <span className='ms-nav__item-text'>{item.linkText}</span>
                        <span className='ms-nav__chevron' aria-hidden='true' />
                    </button>
                );
            } else if (item.linkURL) {
                content = (
                    <a
                        className='ms-nav__item-link'
                        href={item.linkURL}
                        target={item.shouldOpenNewTab ? '_blank' : undefined}
                        rel={item.shouldOpenNewTab ? 'noopener noreferrer' : undefined}
                        {...this._drawerLinkAttributes(item)}
                    >
                        {item.linkText}
                    </a>
                );
            } else {
                content = <span className='ms-nav__item-text'>{item.linkText}</span>;
            }
            return (
                <li key={key} className='ms-nav__item'>
                    {content}
                </li>
            );
        });
    }

    private _drawerLinkAttributes(item: IMenuItemData): React.AnchorHTMLAttributes<HTMLAnchorElement> {
        const linkText = item.linkText || '';
        this.payLoad.contentAction.etext = linkText;
        return {
            ...getTelemetryAttributes(this.telemetryContent, this.payLoad),
            onClick: onTelemetryClick(this.telemetryContent, this.payLoad, linkText)
        };
    }

    private readonly _setDrawerButtonRef = (key: string) => (element: HTMLButtonElement | null): void => {
        if (element) {
            this.drawerButtons.set(key, element);
        } else {
            this.drawerButtons.delete(key);
        }
    };

    private readonly _drawerPush = (index: number) => (): void => {
        this.setState({ drawerPath: [...this.state.drawerPath, index] }, () => {
            const back = this.drawerPanels.current?.querySelector<HTMLButtonElement>('.ms-nav__panel.is-active .ms-nav__back-button');
            back?.focus();
        });
    };

    private readonly _drawerPop = (): void => {
        const { drawerPath } = this.state;
        if (drawerPath.length === 0) {
            return;
        }
        const key = drawerPath.join('-');
        this.setState({ drawerPath: drawerPath.slice(0, -1) }, () => {
            this.drawerButtons.get(key)?.focus();
        });
    };

    /**
     * Method to render mobile menu.
     * @returns Jsx element.
     */
    private _renderMobileMenu(): JSX.Element {
        const { menuItemData, Navigation, config } = this.props;
        return (
            <Module
                {...Navigation}
                key='mobile'
                className={classnames('ms-nav', config.className, 'mobile-vp', 'mobile-view')}
                role='listbox'
            >
                {menuItemData.map((menuItem: IMenuItemData, posinset: number) => {
                    return this._renderDrawerMenu(menuItem, menuItemData.length, posinset + this._positionInSetOffset);
                })}
            </Module>
        );
    }

    /**
     * Method to render desktop menu.
     * @returns Jsx element.
     */
    private _renderDesktopMenu(): JSX.Element {
        const { MenuList, Navigation, config } = this.props;
        return (
            <Module {...Navigation} key='desktop' className={classnames('ms-nav', config.className, 'desktop-vp')} role='navigation'>
                <Node {...MenuList} ref={this.menuNode} tabIndex='-1'>
                    {this._renderDisplay()}
                </Node>
            </Module>
        );
    }

    /**
     * Method to render drawer menu.
     * @param menuItem -Menuitem data.
     * @param setSize -Setsize data.
     * @param posinset -Current position.
     * @returns Jsx element.
     */
    private _renderDrawerMenu(menuItem: IMenuItemData, setSize: number, posinset: number): JSX.Element | null {
        if (menuItem && menuItem.subMenu && ArrayExtensions.hasElements(menuItem.subMenu)) {
            return this._renderDrawer(menuItem, setSize, posinset);
        }
        return this._renderLinkMenuItem(menuItem, undefined, true, false, setSize, posinset);
    }

    /**
     * Method to render drawer component.
     * @param menuItem -Menuitem data.
     * @param setSize -Setsize data.
     * @param posinset -Current position.
     * @returns Jsx element.
     */
    private _renderDrawer(menuItem: IMenuItemData, setSize?: number, posinset?: number): JSX.Element | null {
        const toggleButtonText = menuItem.linkText;
        const keyValue = this.state.drawerKeyValue;
        const buttonText = toggleButtonText !== undefined ? toggleButtonText : '';
        const keys = keyValue !== undefined ? keyValue : {};
        const isMobileView = this._isMobile;

        let isDrawerOpen = false;
        if (keys[buttonText]) {
            isDrawerOpen = true;
        }
        const colProps: ICollapseProps = { isOpen: isDrawerOpen };
        return (
            <Drawer
                role='listbox'
                toggleButtonProps={{
                    'aria-setsize': setSize,
                    'aria-posinset': posinset,
                    role: 'menuitem'
                }}
                ariaLabel={isMobileView ? menuItem.ariaLabel : undefined}
                key={menuItem.id}
                className='ms-nav__drawer'
                openGlyph='ms-nav__drawer-open'
                onToggle={this._onDrawerChange}
                collapseProps={colProps}
                closeGlyph='ms-nav__drawer-close'
                glyphPlacement='end'
                toggleButtonText={this._renderLinkText(toggleButtonText, setSize, posinset)}
            >
                <div role='listbox'>
                    {menuItem.subMenu!.map((menuSubItem: IMenuItemData, currentPos: number) => {
                        if (ArrayExtensions.hasElements(menuSubItem.subMenu)) {
                            return this._renderDrawer(menuSubItem, menuItem.subMenu?.length, currentPos + this._positionInSetOffset);
                        }
                        return this._renderDrawerLink(menuSubItem, menuItem.subMenu?.length, currentPos + this._positionInSetOffset);
                    })}
                </div>
            </Drawer>
        );
    }

    /**
     * Method to render link.
     * @param linkText -Text on Menu link.
     * @param setSize -Setsize data.
     * @param posinset -Current position.
     * @returns Jsx element.
     */
    private _renderLinkText(linkText: string | undefined, setSize: number | undefined, posinset: number | undefined): JSX.Element {
        return (
            <span aria-setsize={setSize} aria-posinset={posinset}>
                {linkText}
            </span>
        );
    }

    /**
     * Method to render drawer link.
     * @param item -Single Menuitem.
     * @param setSize -Setsize data.
     * @param posinset -Current position.
     * @returns Jsx element.
     */
    private _renderDrawerLink(item: IMenuItemData, setSize: number | undefined, posinset: number): JSX.Element | null {
        if (item && item.linkText && item.linkURL && item.linkURL.length > 0) {
            return this._renderLinkMenuItem(item, undefined, true, false, setSize, posinset);
        } else if (item && item.linkText && !item.linkURL) {
            return this._renderSpanMenuItem(item);
        }
        return null;
    }

    /**
     * Method to generate menu.
     * @returns Jsx element.
     */
    private _renderDisplay(): JSX.Element[] {
        const { ListItem, menuItemData } = this.props;
        const isMobileView = this._isMobile;
        const { activeMenu } = this.state;
        const menuItemList: JSX.Element[] = [];

        if (isMobileView && activeMenu !== undefined && menuItemData.length > 0) {
            let menuItem: IMenuItemData = {};
            for (const menuItemDatum of menuItemData) {
                if (menuItemDatum && menuItemDatum.id === activeMenu) {
                    menuItem = menuItemDatum;
                    this.setState({ parentMenu: undefined });
                    break;
                }
                menuItem = this._getFromSubMenu(menuItemDatum) as IMenuItemData;
                if (menuItem && menuItem.id === activeMenu) {
                    break;
                }
            }

            menuItem &&
                menuItemList.push(
                    <Node key={menuItem.id} {...ListItem}>
                        {` `}
                        {this._createMenuItemList(menuItem)}
                        {` `}
                    </Node>
                );
        } else {
            menuItemData.forEach((item: IMenuItemData, index: number) => {
                const hasSubMenu = item.subMenu && item.subMenu.length > 0;
                menuItemList.push(
                    <li
                        key={index}
                        className={ListItem.className}
                        role='presentation'
                        onMouseEnter={hasSubMenu ? this._handleMouseEnter(item) : undefined}
                        onMouseLeave={hasSubMenu ? this._handleMouseLeave : undefined}
                    >
                        {this._createMenuItemList(item)}
                    </li>
                );
            });
        }

        return menuItemList;
    }

    /**
     * Method to get data for submenu.
     * @param item -Single Menuitem.
     * @returns IMenuItemData.
     */
    private _getFromSubMenu(item?: IMenuItemData): IMenuItemData | null {
        const subMenus = item && item.subMenu;
        if (subMenus && subMenus.length > 0) {
            for (let i = 0; i <= subMenus.length - 1; i++) {
                if (subMenus[i].id === this.state.activeMenu) {
                    this.setState({ parentMenu: item?.id });
                    return subMenus[i];
                }
                const found = this._getFromSubMenu(subMenus[i]);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    /**
     * Method to create item list.
     * @param menuItemData -Single Menuitem.
     * @returns Jsx element.
     */
    private _createMenuItemList(menuItemData: IMenuItemData): JSX.Element | null {
        if (menuItemData && menuItemData.subMenu && menuItemData.subMenu.length > 0) {
            if (this._isMobile && this.state.activeMenu !== undefined) {
                return this._renderSubMenu(menuItemData.subMenu, menuItemData.id);
            }
            return (
                <>
                    {this._renderButtonMenuItem(menuItemData)}
                    {this._renderSubMenu(menuItemData.subMenu, menuItemData.id)}
                </>
            );
        } else if (menuItemData && menuItemData.linkText && menuItemData.linkURL && menuItemData.linkURL.length > 0) {
            return this._renderLinkMenuItem(menuItemData, menuItemData.id, false, true);
        } else if (menuItemData && menuItemData.linkText && !menuItemData.linkURL) {
            return this._renderSpanMenuItem(menuItemData, menuItemData.id, true);
        }

        return null;
    }

    private _renderSubMenu(subMenus?: IMenuItemData[], activeMenu?: number, IsSubMenu?: boolean): JSX.Element | null {
        const { ListItem } = this.props;
        const isMobileView = this._isMobile;
        const enableMultiSupportMenu = this.props.config.enableMultilevelMenu || false;
        const subMenuLevel = 3;
        const multiLevelSupportedMenu = this.props.config.menuLevelSupport || subMenuLevel;

        // Const isParentMenu:boolean= false;
        if (activeMenu && this.state.activeMenu !== activeMenu) {
            this.props.context.telemetry.error('Navigation Active menu content is empty, module wont render.');
            return null;
        }

        if (!subMenus || subMenus.length === 0) {
            this.props.context.telemetry.error('Navigation Submenu content is empty, module wont render.');
            return null;
        }

        let levelClassName: string = '';
        const menuOptions =
            subMenus &&
            subMenus.map((option: IMenuItemData, idx: number) => {
                const hasOptions = option.subMenu && option.subMenu.length > 0;
                let menuItem: JSX.Element | null;
                if (hasOptions && isMobileView) {
                    menuItem = this._renderButtonMenuItem(option, activeMenu, idx);
                } else {
                    menuItem = option.linkURL ? this._renderLinkMenuItem(option, idx) : this._renderSpanMenuItem(option);
                }
                let subMenu;
                const haveSubmenu = hasOptions && enableMultiSupportMenu && this.currentLevel <= Math.round(multiLevelSupportedMenu) - 1;
                if (haveSubmenu) {
                    this.currentLevel++;
                    levelClassName = enableMultiSupportMenu ? `level-${this.currentLevel.toString()}` : '';
                    subMenu = this._renderSubMenu(option.subMenu, isMobileView ? option.id : undefined, true);
                }
                return (
                    <Node {...ListItem} key={option.id} className={classnames(ListItem.className, haveSubmenu && 'havesubmenu')}>
                        {menuItem}
                        {subMenu}
                    </Node>
                );
            });
        return this._renderMenu(levelClassName, menuOptions, activeMenu, IsSubMenu);
    }

    /**
     * Method to render button menu item.
     * @param option -Single Menuitem.
     * @param activeMenu -Active menu number.
     * @param index -Active menu index.
     * @returns Jsx element.
     */
    private _renderButtonMenuItem(option: IMenuItemData, activeMenu?: number, index?: number): JSX.Element | null {
        const { Button } = this.props;
        return (
            <Node
                key={index}
                {...Button}
                onClick={this._handleDropdownToggle(option, activeMenu)}
                onFocus={this._closeSubmenu}
                aria-haspopup={!(this.state.activeMenu && this.state.activeMenu === option.id)}
                aria-expanded={!!(this.state.activeMenu && this.state.activeMenu === option.id)}
                data-parent={activeMenu}
            >
                {option.linkText}
            </Node>
        );
    }

    /**
     * Method to render link menu item.
     * @param option -Single Menuitem.
     * @param index -Active menu index.
     * @param hoverEffect -Active menu effect.
     * @param isParent -Is parent menu.
     * @param setSize -Setsize data.
     * @param posinset -Current position.
     * @returns Jsx element.
     */
    private _renderLinkMenuItem(
        option: IMenuItemData,
        index?: number,
        hoverEffect: boolean = true,
        isParent: boolean = false,
        setSize?: number,
        posinset?: number
    ): JSX.Element | null {
        const { Link } = this.props;
        const isMobileView = this._isMobile;
        const linkText = option.linkText ? option.linkText : '';
        const imagesource = option.imageSource ? option.imageSource : '';
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        return (
            <Node
                aria-setsize={isMobileView ? setSize : undefined}
                aria-posinset={isMobileView ? posinset : undefined}
                aria-label={isMobileView ? option.ariaLabel : undefined}
                {...Link}
                key={index}
                onFocus={isParent && this._closeSubmenu}
                target={option.shouldOpenNewTab ? '_blank' : undefined}
                onMouseOver={hoverEffect && this._updateCategoryImage(imagesource, option)}
                href={option.linkURL}
                {...attributes}
                onClick={onTelemetryClick(this.telemetryContent, this.payLoad, linkText)}
            >
                {option.linkText}
            </Node>
        );
    }

    /**
     * Method to render promotional link.
     * @param linkText -Link text.
     * @param linkUrl -Link url.
     * @returns Jsx element.
     */
    private _renderPromotionalLink(linkText?: string, linkUrl?: string): JSX.Element | null {
        const { Link } = this.props;
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        if (linkText && linkUrl) {
            return (
                <Node {...Link} href={linkUrl} {...attributes} onClick={onTelemetryClick(this.telemetryContent, this.payLoad, linkText)}>
                    {linkText}
                </Node>
            );
        }
        return null;
    }

    /**
     * Method to render span menu item.
     * @param option -Single Menu Item.
     * @param index -Index.
     * @param isParent -Is parent menu.
     * @returns Jsx element.
     */
    private _renderSpanMenuItem(option: IMenuItemData, index?: number, isParent: boolean = false): JSX.Element | null {
        const { Span } = this.props;
        return (
            <Node key={index} {...Span} onFocus={isParent && this._closeSubmenu}>
                {option.linkText}
            </Node>
        );
    }

    /**
     * Method to render menu.
     * @param level -Menu level.
     * @param menuOptions -Menu lists.
     * @param currentItem -Menu current.
     * @param submenu -Mneu has submenu or not.
     * @returns Jsx element.
     */
    private _renderMenu(level: string, menuOptions: JSX.Element[], currentItem?: number, submenu?: boolean): JSX.Element | null {
        const { DivContainer, MenuList, ImageDivContainer, showCategoryImage, showPromotionalContent } = this.props;
        const isMobileView = this._isMobile;
        const categoryImageDisplay =
            !isMobileView && showCategoryImage && this.state.categoryImage !== null && this.state.categoryImage !== undefined && !submenu;
        const promotionalContentDisplay =
            !isMobileView && showPromotionalContent && ArrayExtensions.hasElements(this.state.categoryImage) && !submenu;
        const DivContainerClass = this.currentLevel > 2 || categoryImageDisplay ? DivContainer!.className : 'ms-nav__deafult';
        const closingClass = !isMobileView && this.state.isClosing ? 'ms-nav--closing' : '';
        this.currentLevel = 1;
        return (
            <Node {...DivContainer} className={classnames(DivContainerClass, closingClass)}>
                <Node
                    {...MenuList}
                    className={classnames(
                        MenuList.className,
                        level,
                        categoryImageDisplay && 'havecateImage',
                        categoryImageDisplay &&
                            this.props.config.menuLevelSupport &&
                            this.props.config.menuLevelSupport > 2 &&
                            'navmenu-multi-level'
                    )}
                >
                    {menuOptions}
                </Node>
                <div className='category-image-container'>
                    {categoryImageDisplay &&
                        this.state.categoryImage &&
                        this.state.categoryImage.map(item => (
                            <Node key={item.src} {...ImageDivContainer} className={ImageDivContainer!.className}>
                                {this.state.categoryImage && this._getCategoryImage(item)}
                                {promotionalContentDisplay && this._renderPromotionalLink(item.altText, item.additionalProperties?.linkUrl)}
                            </Node>
                        ))}
                </div>
            </Node>
        );
    }

    private readonly _updateCategoryImage = (categoryImageSrc: string, option: IMenuItemData) => () => {
        const linkText = option && option.linkText ? option.linkText : '';
        const promotionalImage: IImageData[] = [{ src: categoryImageSrc, altText: linkText }];

        // Read category and promotional image in one array
        if (ArrayExtensions.hasElements(option.promotionalContent)) {
            option.promotionalContent.map(item => {
                if (item && item.image) {
                    const imageSrc = item.image.src;
                    const promotionalItemImageSettings = item.image.imageSettings;
                    promotionalImage.push({
                        src: imageSrc,
                        altText: item.text,
                        imageSettings: promotionalItemImageSettings,
                        additionalProperties: { linkUrl: item.linkUrl.destinationUrl }
                    });
                }
            });
        }
        this.setState({
            categoryImage: promotionalImage.length > 0 ? promotionalImage : [{ src: 'empty' }],
            categoryImageAltText: linkText
        });
    };

    /**
     * Method to handle dropdwon change.
     * @param data -Menuitem data.
     * @param parentId -Menu parent.
     * @returns Jsx element.
     */
    private readonly _handleDropdownToggle = (data: IMenuItemData, parentId?: number) => () => {
        if (!this._isMobile) {
            this.setState({
                activeMenu: this.state.activeMenu && this.state.activeMenu === data.id! ? undefined : data.id!,
                parentMenu: parentId
            });
            if (this.props.showCategoryImage) {
                this._updateCategoryImage(data.imageSource!, data)();
            }
        } else {
            this.setState({
                activeMenu: data.id,
                parentMenu: parentId
            });
        }

        this._resetFocus();
    };

    private readonly _handleMouseEnter = (data: IMenuItemData) => () => {
        if (!this._isMobile) {
            if (this.closeTimeout) {
                clearTimeout(this.closeTimeout);
                this.closeTimeout = null;
            }
            this.setState({
                activeMenu: data.id!,
                parentMenu: undefined,
                isClosing: false
            });
            if (this.props.showCategoryImage) {
                this._updateCategoryImage(data.imageSource!, data)();
            }
        }
    };

    private readonly _handleMouseLeave = () => {
        if (!this._isMobile && this.state.activeMenu !== undefined) {
            this.setState({ isClosing: true });
            this.closeTimeout = setTimeout(() => {
                this.setState({
                    activeMenu: undefined,
                    categoryImage: null,
                    isClosing: false
                });
                this.closeTimeout = null;
            }, 240);
        }
    };

    private readonly _resetFocus = () => {
        if (this._isMobile) {
            setTimeout(() => {
                this.menuNode && this.menuNode.current && this.menuNode.current.focus();
            }, 0);
        }
    };

    /**
     * Method to handle click outside of menu.
     * @param event -HTML event.
     */
    private readonly _handleClickOutside = (event: MouseEvent) => {
        if (this._isDrawerStyle) {
            return;
        }
        if (this.menuNode.current && !this.menuNode.current.contains(event.target as Node)) {
            this.setState({
                activeMenu: undefined,
                categoryImage: null
            });
        }
    };

    /**
     * Method to handle click outside of menu.
     * @param event -HTML event.
     */
    private readonly _handleFocusOutside = (event: FocusEvent) => {
        if (this._isDrawerStyle) {
            return;
        }
        if (this.menuNode.current && !this.menuNode.current.contains(event.relatedTarget as Node)) {
            this._closeSubmenu();
        }
    };

    /**
     * Method to handle close submenu event.
     */
    private _closeSubmenu(): void {
        if (!this._isMobile) {
            this.setState({ activeMenu: undefined });
        }
    }

    /**
     * Method to handle escape key event.
     * @param event -HTML event.
     */
    private readonly _escFunction = (event: React.KeyboardEvent) => {
        if (event.keyCode !== this.escapeKey) {
            return;
        }
        if (this._isDrawerStyle) {
            if (this.state.drawerPath.length > 0) {
                event.stopPropagation();
                this._drawerPop();
            }
            return;
        }
        if (event.keyCode === this.escapeKey) {
            if (!this._isMobile) {
                const navDrawerList = Array.from(this.menuNode.current!.childNodes);
                for (const item of navDrawerList) {
                    const navDrawerButton = item.firstChild as HTMLButtonElement;
                    if (navDrawerButton.getAttribute('aria-expanded') === 'true') {
                        navDrawerButton.focus();
                    }
                }
            }
            this._closeSubmenu();
        }
    };

    private readonly _getCategoryImage = (categoryImage?: IImageData): React.ReactNode | null => {
        if (!categoryImage || !categoryImage.src) {
            return null;
        }

        const categoryImageUrl = generateImageUrl(categoryImage.src, this.props.context.actionContext.requestContext.apiSettings);
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: 'w=300&h=250&m=8', w: 0, h: 0 },
                sm: { q: 'w=300&h=250&m=8', w: 0, h: 0 },
                md: { q: 'w=300&h=250&m=8', w: 0, h: 0 },
                lg: { q: 'w=300&h=250&m=8', w: 0, h: 0 }
            },
            lazyload: true
        };
        if (categoryImageUrl !== undefined) {
            const ImageData: IImageData = { src: categoryImageUrl };
            return (
                <Image
                    requestContext={this.props.context.actionContext.requestContext}
                    className='ms-nav-image__item'
                    {...ImageData}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={categoryImage.imageSettings ?? this.props.config.categoryImageSettings ?? defaultImageSettings}
                    loadFailureBehavior='hide'
                    role='tabpanel'
                    id={`${categoryImage.src}__categoryImage`}
                    altText={categoryImage.altText}
                />
            );
        }
        return null;
    };

    /**
     * Method to handle escape key event.
     * @param drawerState -HTML event.
     */
    private readonly _onDrawerChange = (drawerState: IDrawerState) => {
        const { drawerKeyValue } = this.state;
        const drawerText: string = drawerState.buttonText !== undefined ? drawerState.buttonText : '';
        const newPair = { [drawerText]: drawerState.isOpen };
        this.setState({ drawerKeyValue: { ...drawerKeyValue, ...newPair } });
    };
}

export default NavigationMenuView;
